import React, { useEffect, useCallback, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers'
import moment from 'moment'
import store from 'store'
import { useNavigate } from 'react-router-dom'
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'

import Input from 'components/UI/Input'
import DropDown from 'components/Input/DropDown'
import SearchableDropDown from 'components/Input/SearchableDropDown'
import Button from 'components/UI/Button'
import Radio from 'components/UI/Radio'
import ErrorMessageWithExclamation from 'components/UI/ErrorMessageWithExclamation'
import UploadAvatar from '../../Upload/UploadAvatar'
import LanguageSelect from '../../Shared/LanguageSelect'
import NotChangeableFieldLabel from '../../Shared/NotChangeableFieldLabel'
import CountryDropdowns from './CountryDropdowns'
import useRegistrationTokenSync from './AboutFormSubmitFix'

import { getUser, updateRegistraionAbout } from '../../../routing/fetchingfunction/user/user.action'
import {
  selectIsLoading,
  selectErrorsUpdateAbout,
  selectUserData,
  selectAccessToken,
  selectTokenSyncComplete,
} from 'routing/fetchingfunction/user/user.reducer'

import { useGetInfo } from 'hooks'

import { AboutScheme } from '../../../routing/utils/validators'

import { compareTimeZones } from 'helpers'

import {
  dateFormats,
  displayLastNameOptions,
  displayNameOptions,
  formattedCountryCodes,
  genders,
  TUTOR_ROLE,
  BACKEND_REGISTRATION_CODES,
} from '../../../constants'
import getFlags from 'helpers/getFlagurl'

const About = ({ role = 'student' }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Use the token synchronization hook to ensure tokens are in sync
  // This prevents 401 errors during form submission
  const tokensAvailable = useRegistrationTokenSync()

  const userData = useSelector(selectUserData)
  const isLoading = useSelector(selectIsLoading)
  const updateAboutErrors = useSelector(selectErrorsUpdateAbout)
  const accessToken = useSelector(selectAccessToken)
  const tokenSyncComplete = useSelector(selectTokenSyncComplete)
  const [isFetchingUser, setIsFetchingUser] = useState(false)
  const [initialUserFetchAttempted, setInitialUserFetchAttempted] = useState(false)
  const [submissionError, setSubmissionError] = useState(null)
  const [formPrePopulated, setFormPrePopulated] = useState(false)

  // Get formatted data for dropdowns with debugging
  const { formattedCountries, formattedWorldTimezones, countries, worldTimezones } = useGetInfo()
  const [selectedDialCode, setSelectedDialCode] = useState('+1') // Default to US dial code

  const handleCountryChange = (selectedCode) => {
    const selectedCountry = formattedCountryCodes.find((c) => c.code === selectedCode)
    if (selectedCountry) {
      setSelectedDialCode(selectedCountry.dialCode)
    }
  }

  // Debug dropdown data
  useEffect(() => {
    console.log('About component - Countries data:', countries?.length || 0)
    console.log('About component - Formatted countries:', formattedCountries?.length || 0)
    console.log('About component - Timezones data:', worldTimezones?.length || 0)
    console.log('About component - Formatted timezones:', formattedWorldTimezones?.length || 0)
  }, [countries, formattedCountries, worldTimezones, formattedWorldTimezones])

  // Function to find country PK by ISO code
  const findCountryPkByCode = useCallback(
    (isoCode) => {
      if (!isoCode || !countries || !Array.isArray(countries)) return null

      // Find the country object with matching ISO code
      const country = countries.find(
        (c) => c.iso_3166_1_a2 === isoCode || c.iso_3166_1_a3 === isoCode || c.code === isoCode
      )

      // return country ? country.id || country.pk : null
      return country ? country.name || country.printable_name : null
    },
    [countries]
  )
  console.log(updateAboutErrors)
  // Function to find timezone PK by name
  const findTimezonePkByName = useCallback(
    (timezoneName) => {
      if (!timezoneName || !worldTimezones || !Array.isArray(worldTimezones)) return null

      // Find the timezone object with matching name
      const timezone = worldTimezones.find(
        (tz) => tz.name === timezoneName || tz.value === timezoneName
      )

      return timezone ? timezone.id || timezone.pk : null
    },
    [worldTimezones]
  )

  // Fetch user data if not already present and we have an access token
  useEffect(() => {
    const currentUserId = userData?.id || userData?.pk

    // Only try to fetch user if:
    // 1. We don't have a user ID in Redux userData.
    // 2. We are not already fetching.
    // 3. An accessToken actually exists.
    // 4. Token synchronization in App.js is complete.
    // 5. We haven't attempted this initial fetch yet.
    if (
      !currentUserId &&
      !isFetchingUser &&
      accessToken &&
      tokenSyncComplete &&
      !initialUserFetchAttempted
    ) {
      console.log(
        'About.jsx: User data not found, attempting initial fetch. Dispatching getUser().'
      )
      setIsFetchingUser(true)
      setInitialUserFetchAttempted(true)

      // Ensure we're using clean versions of the tokens from Redux
      // rather than potentially corrupted localStorage versions
      const cleanTokens = {
        access: accessToken,
        refresh: store.get('refresh_token'),
      }

      // Store these clean tokens to localStorage to ensure consistency
      localStorage.setItem('access_token', cleanTokens.access)
      if (cleanTokens.refresh) {
        localStorage.setItem('refresh_token', cleanTokens.refresh)
      }

      // Now dispatch getUser with error handling
      dispatch(getUser())
        .unwrap()
        .catch((error) => {
          console.error('About.jsx: getUser() dispatch failed after unwrap:', error)

          // Instead of triggering a redirect on error, let's try to use fallback user data
          // or create a minimal user data structure that allows the form to be submitted
          if (!store.get('user_data')) {
            console.log(
              'About.jsx: No user data available. Creating minimal user data for form submission.'
            )
            const minimalUserData = {
              id: store.get('user_id'), // This might be stored during registration/activation
              email: store.get('user_email'), // This might be stored during registration
              role: store.get('user_role') || 'student',
            }

            // Only set this if we have at least an ID to work with
            if (minimalUserData.id) {
              store.set('user_data', minimalUserData)
              // Don't update Redux state here as that would normally happen through the action
            }
          }
        })
        .finally(() => {
          setIsFetchingUser(false)
          console.log('About.jsx: getUser() dispatch finished (finally block).')
        })
    } else if (!tokenSyncComplete && accessToken) {
      console.log('About.jsx: AccessToken exists, but token sync is not complete. Waiting...')
    } else if (!accessToken && !currentUserId && tokenSyncComplete) {
      console.log(
        'About.jsx: No access token (and no user ID) even after sync. Skipping getUser(). Potential logout/redirect needed.'
      )
    } else if (currentUserId) {
      console.log('About.jsx: User data with ID already present. Skipping getUser().')
    }
  }, [dispatch, userData, isFetchingUser, accessToken, tokenSyncComplete])

  // Set up form with proper default values for all fields
  const {
    register,
    handleSubmit,
    formState: { errors },
    resetField,
    control,
    setValue,
    watch,
    clearErrors,
    setError,
  } = useForm({
    resolver: yupResolver(AboutScheme),
    defaultValues: {
      user_profile_languages: [],
      country_code: '',
      gender: null,
      timezone: '',
      language: '',
      phone_number_part: '',
      birth_date: null,
      country_of_origin: '',
      country_of_residence: '',
      time_format: '12',
      profile_picture: null,
    },
    mode: 'onChange',
  })
  // Pre-populate form with existing user data
  useEffect(() => {
    if (userData?.profile && !formPrePopulated && !isLoading) {
      console.log('Pre-populating form with user profile data:', userData.profile)

      // Set values only if they exist in the profile
      const updates = {}

      // Only update if value exists
      if (userData.profile.gender) {
        updates.gender = userData.profile.gender
      }

      if (userData.profile.birth_date) {
        try {
          const parsedDate = moment(userData.profile.birth_date)
          if (parsedDate.isValid()) {
            updates.birth_date = parsedDate
          }
        } catch (e) {
          console.error('Failed to parse birth date:', e)
        }
      }

      if (userData.profile.country_code) {
        updates.country_code = userData.profile.country_code
      }

      if (userData.profile.phone_number_part) {
        updates.phone_number_part = userData.profile.phone_number_part
      }

      // Handle country of origin (could be an ID or ISO code)
      if (userData.profile.country_of_origin) {
        updates.country_of_origin = userData.profile.country_of_origin
      } else if (userData.profile.country_of_origin_detail?.iso_3166_1_a2) {
        updates.country_of_origin = userData.profile.country_of_origin_detail.iso_3166_1_a2
      }

      // Handle country of residence (could be an ID or ISO code)
      if (userData.profile.country_of_residence) {
        updates.country_of_residence = userData.profile.country_of_residence
      } else if (userData.profile.country_of_residence_detail?.iso_3166_1_a2) {
        updates.country_of_residence = userData.profile.country_of_residence_detail.iso_3166_1_a2
      }

      // Handle timezone (could be an ID or name)
      if (userData.profile.timezone) {
        updates.timezone = userData.profile.timezone
      } else if (userData.profile.timezone_detail?.name) {
        updates.timezone = userData.profile.timezone_detail.name
      }

      // Handle time format
      if (userData.profile.time_format) {
        updates.time_format = userData.profile.time_format
      }

      // Handle languages
      if (Array.isArray(userData.profile.languages) && userData.profile.languages.length > 0) {
        updates.user_profile_languages = userData.profile.languages
      } else if (
        Array.isArray(userData.profile.user_profile_languages) &&
        userData.profile.user_profile_languages.length > 0
      ) {
        updates.user_profile_languages = userData.profile.user_profile_languages
      }

      // Batch update all values at once to prevent multiple re-renders
      if (Object.keys(updates).length > 0) {
        // Use setTimeout to ensure the form is mounted before setting values
        setTimeout(() => {
          Object.entries(updates).forEach(([key, value]) => {
            setValue(key, value, { shouldValidate: false, shouldDirty: false })
          })
          setFormPrePopulated(true)
          console.log('Form pre-populated successfully with updates:', updates)
        }, 0)
      } else {
        setFormPrePopulated(true)
        console.log('No profile updates to apply')
      }
    }
  }, [userData, setValue, formPrePopulated, isLoading])

  const profile_picture = watch('profile_picture')
  const gender = watch('gender')

  const isTutor = role === TUTOR_ROLE

  const handleSubmitForm = useCallback(
    async (data) => {
      // Clear any previous submission errors
      setSubmissionError(null)

      const displayName = store.get('display_name') || displayNameOptions[0].value
      store.remove('display_name')

      const freshUserData = store.get('user_data')
      const freshUserRole = store.get('user_role')
      const userId = freshUserData?.id || freshUserData?.pk || userData?.id || userData?.pk
      const userRole = freshUserRole
      if (!userId) {
        console.error('User ID not available for profile update at submission time.')
        // Set local error state and dispatch an action that the reducer can use to set an error message in the store
        setSubmissionError('User data not available. Please refresh and try again.')
        dispatch({
          type: 'user/updateAbout/rejected',
          payload: {
            message: 'User data not available. Please refresh and try again.',
          },
        })
        return
      }

      try {
        // Ensure birth_date is properly formatted for backend
        let formattedBirthDate = null
        if (data.birth_date) {
          try {
            // Handle various date formats
            let parsedDate

            if (moment.isMoment(data.birth_date)) {
              parsedDate = data.birth_date
            } else if (data.birth_date instanceof Date && !isNaN(data.birth_date)) {
              parsedDate = moment(data.birth_date)
            } else if (typeof data.birth_date === 'string') {
              // Try parsing with multiple formats

              parsedDate = moment(data.birth_date, [
                'YYYY-MM-DD',
                'DD/MM/YYYY',
                'MM/DD/YYYY',
                'YYYY/MM/DD',
              ])
            }

            // Validate the parsed date
            if (parsedDate && parsedDate.isValid()) {
              // Ensure the date is reasonable (not in the future and not too old)
              const today = moment()
              const minDate = moment().subtract(150, 'years')
              const maxDate = moment().subtract(13, 'years') // Minimum age 13

              if (parsedDate.isAfter(maxDate)) {
                throw new Error('Date of birth indicates age under 13')
              }

              if (parsedDate.isBefore(minDate)) {
                throw new Error('Date of birth is too far in the past')
              }

              formattedBirthDate = parsedDate.format('YYYY-MM-DD')
            } else {
              console.warn('Invalid date format detected')
              throw new Error('Invalid date format')
            }
          } catch (e) {
            console.error('Failed to parse birth date:', e)
            setSubmissionError('Please enter a valid date of birth')
            return
          }
        }

        // Explicitly set current_registration_step to the next step based on role
        // instead of just profile_update
        const nextStepCode =
          role === TUTOR_ROLE
            ? BACKEND_REGISTRATION_CODES.TUTOR_TEST
            : BACKEND_REGISTRATION_CODES.STUDENT_GOALS

        // Set up the navigation route for after form submission
        const nextRoute =
          role === TUTOR_ROLE ? '/tutor/register/tutor-test' : '/student/register/goals'

        console.log(
          `[STEP TRACKING] ABOUT FORM: Current role is "${role}", setting next step to "${nextStepCode}", will navigate to "${nextRoute}"`
        )

        const profileData = {
          user: userId,
          profile: {
            // ...data,
            // user: userId,
            time_format: data.time_format,
            gender: data.gender,
            country_code: data.country_code,
            phone_number_part: data.phone_number_part,
            display_name: displayName,
            display_last_name: displayLastNameOptions[0].value,
            date_format: dateFormats[0].value,
            birth_date: formattedBirthDate,
            // Convert country ISO codes to primary keys
            country_of_origin: data.country_of_origin,
            country_of_residence: data.country_of_residence,

            // Convert timezone name to primary key
            // timezone: findTimezonePkByName(data.timezone),
            timezone: data.timezone,
            languages: data.user_profile_languages,
            // Clean user_profile_languages to remove null values and ensure proper format
            user_profile_languages: Array.isArray(data.user_profile_languages)
              ? data.user_profile_languages
                  .filter((lang) => lang !== null && lang !== undefined) // Remove null/undefined
                  .map((lang) => {
                    // If it's an object with a value property, extract that value
                    if (typeof lang === 'object' && lang !== null) {
                      return { language: lang.language, level: lang.level }
                    }
                    // Otherwise, just use the value as is (if it's a string)
                    return typeof lang === 'string' ? lang : null
                  })
                  .filter(Boolean) // Remove any resulting null values
              : [],
          },
          // Set the registration step to the next step directly
          registration_step: nextStepCode,
          ...(userRole === 'student' && {
            user_student_profile: (() => {
              const { profile_picture, ...rest } = userData.user_student_profile
              return rest
            })(),
          }),
          ...(userRole === 'tutor' && {
            user_tutor_profile: (() => {
              const { profile_picture, ...rest } = userData.user_tutor_profile
              return rest
            })(),
          }),
        }

        const formData = new FormData()
        // Append all fields to For mData
        // Object.entries(profileData).forEach(([key, value]) => {
        //   console.log('itereate', key, value)
        //   if (Array.isArray(value)) {
        //     value.forEach((item, index) => {
        //       if (typeof item === 'object' && item !== null) {
        //         Object.entries(item).forEach(([subKey, subValue]) => {
        //           formData.append(`${key}[${index}][${subKey}]`, subValue)
        //           console.log(subValue, 'subvalue')
        //         })
        //       } else {
        //         formData.append(`${key}[${index}]`, item)
        //       }
        //     })
        //   } else if (typeof value === 'object' && value !== null) {
        //     Object.entries(value).forEach(([subKey, subValue]) => {
        //       if (subKey === 'profile_picture') {
        //         subValue && formData.append(`${key}[${subKey}]`, subValue?.[0])
        //       } else {
        //         if (Array.isArray(subValue)) {
        //           subValue.forEach((item, index) => {
        //             if (typeof item === 'object' && item !== null) {
        //               Object.entries(item).forEach(([subK, subV]) => {
        //                 formData.append(`${key}[${subKey}][${index}][${subK}]`, subV)
        //                 console.log(subValue, 'subvalue')
        //               })
        //             } else {
        //               formData.append(`${key}[${index}]`, item)
        //             }
        //           })
        //         } else {
        //           formData.append(`${key}[${subKey}]`, subValue)
        //         }
        //       }
        //     })
        //   } else {
        //     formData.append(key, value)
        //   }
        // })

        // Object.entries(profileData.profile).forEach(([key, value]) => {
        //   if (Array.isArray(value)) {
        //     value.forEach((item, index) => {
        //       Object.entries(item).forEach(([subKey, subValue]) => {
        //         formData.append(`profile[${key}][${index}][${subKey}]`, subValue)
        //       })
        //     })
        //   } else {
        //     formData.append(`profile[${key}]`, value)
        //   }
        // })

        Object.entries(profileData).forEach(([key, value]) => {
          if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value))
          } else {
            formData.append(key, value)
          }
        })

        formData.append('registration_step', nextStepCode)
        formData.append('user', userId)
        data.profile_picture?.[0] && isTutor
          ? formData.append('user_tutor_profile[profile_picture]', data.profile_picture?.[0])
          : formData.append('user_student_profile[profile_picture]', data.profile_picture?.[0])

        // PROFILE (NO user inside profile)
        Object.entries(profileData.profile).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item, index) => {
              if (typeof item === 'object' && item !== null) {
                Object.entries(item).forEach(([subKey, subValue]) => {
                  formData.append(`profile[${key}][${index}][${subKey}]`, subValue)
                })
              }
            })
          } else {
            formData.append(`profile[${key}]`, value)
          }
        })

        // formData.append('profile', JSON.stringify(profileData.profile))
        // formData.append('registration_step', JSON.stringify(profileData.registration_step))

        // profileData?.user_student_profile &&
        //   formData.append('user_student_profile', JSON.stringify(profileData.user_student_profile))

        // profileData?.user_tutor_profile &&
        //   formData.append('user_tutor_profile', JSON.stringify(profileData.user_tutor_profile))
        // console.log(Object.fromEntries(formData))

        // Debug logging for troubleshooting
        console.log('About form submission - cleaned data:', {
          userId,
          ...profileData,
        })

        console.log(
          `[STEP TRACKING] ABOUT FORM: Submitting profile update with registration_step="${nextStepCode}"`
        )

        dispatch(updateRegistraionAbout(formData))
          .unwrap()
          .then((result) => {
            sessionStorage.setItem('register_step', nextStepCode)
            navigate(nextRoute)
          })
          .catch((error) => {
            console.error('[STEP TRACKING] ERROR: Failed to update profile in About.jsx:', error)
            setSubmissionError(error.message || 'An error occurred while updating your profile.')
            dispatch({
              type: 'user/updateAbout/rejected',
              payload: {
                message: error.message || 'An error occurred while updating your profile.',
              },
            })
          })
      } catch (error) {
        console.error('[STEP TRACKING] ERROR: Exception in About.jsx handleSubmitForm:', error)
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          'An unexpected error occurred while updating your profile.'
        setSubmissionError(errorMessage)
        dispatch({
          type: 'user/updateAbout/rejected',
          payload: { message: errorMessage },
        })
      }
    },
    [dispatch, role, userData, navigate, findCountryPkByCode, findTimezonePkByName]
  )

  const handleResetAvatar = useCallback(() => {
    resetField('profile_picture')
  }, [resetField])

  // Register custom fields once on component mount
  useEffect(() => {
    // Register all fields that need special handling
    const fields = [
      'user_profile_languages',
      'country_code',
      'country_of_origin',
      'country_of_residence',
      'timezone',
      'time_format',
      'profile_picture',
    ]

    fields.forEach((field) => register(field))

    // Set default gender if not set
    // if (!gender) {
    //   setValue('gender', 'Unknown', { shouldValidate: false })
    // }
  }, [register, setValue, gender])

  useEffect(() => {
    if (profile_picture && profile_picture[0] && !profile_picture[0].type.includes('image')) {
      setValue('profile_picture', null)
    }
  }, [profile_picture, setValue])

  return (
    <form
      onSubmit={handleSubmit(handleSubmitForm)}
      className="bg-[#FEEBDD] rounded-lg py-16 px-10 sm:px-20 md:px-50 lg:px-100 xl:px-[148px] text-[#F67443] max-w-[800px] w-full mx-auto my-16 flex flex-col items-center justify-start"
    >
      <h2 className="lg:text-3xl text-2xl font-semibold text-center mb-8">Tell Us About You</h2>

      {/* Show submission error if it exists */}
      {submissionError && (
        <div className="w-full mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          <p className="font-bold">Submission Error</p>
          <p>{submissionError}</p>
        </div>
      )}

      {!tokensAvailable && (
        <div className="w-full mb-6 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
          <p className="font-bold">Authentication Issue</p>
          <p>Session may have expired. Please refresh the page or try logging in again.</p>
        </div>
      )}

      <div className="mb-6  flex flex-col items-center justify-start">
        <h3 className="mb-3">Upload Your Photo</h3>

        {role === 'tutor' && (
          <span className="text-xs text-primary-75 !mb-6">
            You need to use your real photo to create account
          </span>
        )}

        <UploadAvatar gender={gender} picture={profile_picture} onResetAvatar={handleResetAvatar}>
          <input className="sr-only" type="file" {...register('profile_picture')} />
        </UploadAvatar>

        <div className="flex flex-col items-center justify-start text-xs text-primary-75">
          <span className="mb-[6px]">Max. size can't be more than 2 MB</span>

          <span>Supports: JPG, PNG, and GIF Formats</span>
        </div>
      </div>

      <div className="flex flex-col items-start justify-start w-full mb-8">
        <div className="flex flex-col items-start justify-start w-full !mb-4">
          <h3 className="mt-8 !mb-4 text-xl font-bold !pb-2 border-b border-b-primary-600 w-full">
            Personal Information
          </h3>

          {updateAboutErrors && (
            <div className="!mb-4 text-error-500">
              <p className="font-bold">Following errors occur</p>
              <ErrorMessageWithExclamation text={updateAboutErrors} />
            </div>
          )}

          <div className="flex sm:flex-row   items-start justify-start w-full gap-2 sm:gap-4">
            <div className="max-w-[135px] w-full text-primary-100 !mr-2">
              <span className="inline-block text-sm font-medium mb-[6px]">Country Code </span>

              <Controller
                control={control}
                name="country_code"
                render={({ field: { value, onChange } }) => {
                  return (
                    <SearchableDropDown
                      autoComplete={true}
                      className="!h-[46px]"
                      value={value}
                      setValue={(newValue) => {
                        onChange(newValue)
                        handleCountryChange(newValue)
                      }}
                      menuItems={formattedCountryCodes}
                      renderItem={(item) => (
                        <div className="flex items-center gap-2  h-full w-full">
                          <img src={getFlags(item?.key)} className="w-5 h-5" alt="" />
                          <span className="text-primary-100">{item?.label}</span>
                        </div>
                      )}
                      searchKeys={['searchText']}
                      placeholder="Select"
                    />
                  )
                }}
              />

              {errors.country_code && (
                <ErrorMessageWithExclamation text={errors.country_code.message} />
              )}
            </div>

            <div className="flex-1">
              <span className="inline-block text-sm font-medium mb-[6px]">Phone Number</span>

              <Input
                className="w-full text-primary-600"
                wrapperClassName="mb-[6px]"
                placeholder="Phone Number"
                {...register('phone_number_part', {
                  required: 'Phone number is required',
                })} //moved validation to valdators.js
              />
              {errors.phone_number_part && (
                <ErrorMessageWithExclamation text={errors.phone_number_part.message} />
              )}

              <span className="text-xs text-primary-75 block">
                This is a hidden field. Your telephone number will be used for SMS 2FA activation
                and notifications.
              </span>
            </div>
          </div>
        </div>

        {/* Use the direct REST Countries API component for country selection */}
        <CountryDropdowns
          control={control}
          errors={errors}
          Controller={Controller}
          isTutor={isTutor}
        />
        <div className="w-full flex flex-col items-start !mb-4">
          <NotChangeableFieldLabel isLabel={true} show={isTutor}>
            Date of Birth
          </NotChangeableFieldLabel>

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Controller
              control={control}
              name="birth_date"
              rules={{
                required: 'Date of birth is required',
                validate: (value) => {
                  const age = moment().diff(value, 'years')
                  if (role === TUTOR_ROLE && age < 18) return 'You must be at least 18 years old'
                  if (role !== TUTOR_ROLE && age < 18) return 'You must be at least 18 years old'
                  return true
                },
              }}
              render={({ field: { value, onChange } }) => {
                return (
                  <DatePicker
                    slots={{
                      openPickerIcon: () => (
                        <div aria-hidden="true" className="">
                          <img width={20} height={20} src="/assets/image/calendar.png" alt="" />
                        </div>
                      ),
                    }}
                    minDate={moment().subtract(150, 'years')}
                    maxDate={moment().subtract(role === TUTOR_ROLE ? 18 : 18, 'years')}
                    value={value}
                    onChange={onChange}
                    className="custom-date-picker !text-red-600 !w-full"
                    slotProps={{
                      textField: {
                        readOnly: false,
                        placeholder: 'Date of Birth',
                        format: 'DD/MM/YYYY',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            height: '46px',
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            border: '1px solid #FFCA84',
                            '& fieldset': {
                              borderColor: 'transparent',
                            },
                            '&:hover fieldset': {
                              borderColor: 'transparent',
                              // backgroundColor: '#FFCA8433',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'transparent',
                            },
                            '& input': {
                              color: '#F67443',
                              fontFamily: 'inherit',
                            },
                            '& input::placeholder': {
                              color: '#F67443',
                              fontFamily: 'inherit',
                            },
                            '& .MuiSvgIcon-root': {
                              color: '#F79009',
                            },
                            '& .MuiPickersCalendarHeader-root': {
                              backgroundColor: '#111',
                              color: '#fff',
                              padding: '12px 16px',
                            },
                          },
                        },
                      },
                      calendarHeader: {
                        sx: {
                          backgroundColor: '#FFCA8433',
                          color: '#F67443',
                          padding: '12px 16px',

                          '& .MuiPickersCalendarHeader-label': {
                            fontWeight: 600,
                          },

                          '& .MuiPickersArrowSwitcher-button': {
                            color: '#fff',
                          },
                          '& .MuiPickersArrowSwitcher-root': {
                            backgroundColor: '#11F674433',
                            color: 'black',
                          },
                          '& .MuiButtonBase-root': {
                            backgroundColor: '#FFCA8433',
                            color: '#F67443',
                          },
                        },
                      },
                      popper: {
                        sx: {
                          // style the entire calendar root
                          '& .MuiDateCalendar-root': {
                            backgroundColor: '#FFCA8433', // whole calendar background
                            color: '#fff', // text color by default
                            borderRadius: '8px',
                            padding: '8px',
                          },

                          // style the view transition container
                          '& .MuiDateCalendar-viewTransitionContainer': {
                            backgroundColor: '#FFCA8433',
                          },

                          // style day cells
                          '& .MuiPickersDay-root': {
                            color: '#F67443',
                          },

                          '& .MuiPickersYear-root .MuiPickersYear-yearButton': {
                            backgroundColor: '#FFCA8433',
                            color: '#F67443',
                          },
                        },
                      },
                    }}
                    format="DD/MM/YYYY"
                  />
                )
              }}
            />
          </LocalizationProvider>
          <span className="text-xs text-primary-75 mt-1">
            {isTutor && 'You need to use your real birthday to pass KYC.'}
          </span>

          {errors.birth_date && <ErrorMessageWithExclamation text={errors.birth_date.message} />}
        </div>

        <div className="flex flex-col items-start justify-start w-full">
          <NotChangeableFieldLabel isLabel={false} show={isTutor}>
            Select Gender
          </NotChangeableFieldLabel>

          <Controller
            control={control}
            name="gender"
            render={({ field: { value, onChange } }) => {
              return (
                <DropDown
                  className="!mb-[6px]"
                  placeholder="Gender"
                  menuItems={genders}
                  value={value}
                  setValue={onChange}
                />
              )
            }}
          />
          <span className="text-xs text-primary-75">
            {isTutor ? 'This field will be hidden to your students' : 'This is a hidden field'}
          </span>
          {errors.gender && <ErrorMessageWithExclamation text={errors.gender.message} />}
        </div>
      </div>

      <div className="w-full mb-8">
        <h3 className="!mb-4 text-xl font-bold !pb-2 border-b border-b-primary-600 w-full">
          Language Spoken
        </h3>

        <p className="text-sm font-medium my-3">
          {isTutor
            ? 'All Information below is visible to all students'
            : 'All information below is visible to your tutor'}
        </p>

        <Controller
          control={control}
          name="user_profile_languages"
          render={({ field: { value, onChange } }) => {
            return (
              <LanguageSelect
                value={value}
                clearErrors={clearErrors}
                setError={setError}
                onChange={onChange}
              />
            )
          }}
        />

        {errors.user_profile_languages && (
          <ErrorMessageWithExclamation text={errors.user_profile_languages.message} />
        )}
      </div>

      <div className="w-full pb-3">
        <h3 className="!mb-4 text-xl font-bold !pb-2 border-b border-b-primary-600 w-full">
          Time Settings
        </h3>

        <div className="!mb-4">
          <span className="inline-block text-sm font-mediumm mt-1.5 mb-1">Time Format</span>

          <div className="w-full grid grid-cols-2 gap-x-4">
            <Radio
              wrapperClassName="border !border-primary-450 rounded-lg bg-white !py-3 !px-4 hover:cursor-pointer"
              labelClassName="flex-1"
              label="12-hour"
              value="12"
              {...register('time_format', {
                required: 'Please select a time format',
              })}
            />
            <Radio
              wrapperClassName="border !border-primary-450 rounded-lg bg-white !py-3 !px-4 hover:cursor-pointer"
              labelClassName="flex-1"
              label="24-hour"
              value="24"
              {...register('time_format', {
                required: 'Please select a time format',
              })}
            />
          </div>
          {errors.time_format && <ErrorMessageWithExclamation text={errors.time_format.message} />}
        </div>

        <div className="flex flex-col items-start justify-start w-full !mb-4">
          <span className="inline-block text-sm font-medium mb-[6px]">Select Time Zone</span>

          <Controller
            name="timezone"
            control={control}
            rules={{ required: 'Timezone is required' }}
            render={({ field: { onChange, value } }) => {
              // Ensure we have items to display in the dropdown
              const dropdownItems =
                Array.isArray(formattedWorldTimezones) && formattedWorldTimezones.length > 0
                  ? formattedWorldTimezones.sort(compareTimeZones)
                  : [
                      {
                        key: 'loading_tz',
                        label: 'Loading timezones...',
                        value: '',
                      },
                    ]

              return (
                <DropDown
                  className="!mb-[6px]"
                  placeholder="Select Time Zone"
                  menuItems={dropdownItems}
                  value={value}
                  setValue={onChange}
                />
              )
            }}
          />
          <span className="text-xs text-primary-75">
            {isTutor
              ? 'This field is visible to all students'
              : 'This field is visible to your tutor.'}
          </span>
          {errors.timezone && <ErrorMessageWithExclamation text={errors.timezone.message} />}
        </div>
      </div>

      <Button
        className="w-full font-semibold border !border-gray-500/30"
        type="submit"
        isLoading={isLoading}
        disabled={
          isLoading || isFetchingUser || !tokensAvailable || !(userData?.id || userData?.pk)
        }
      >
        Continue
      </Button>
    </form>
  )
}

export default About
